import React from 'react';
import { __ } from '../../helpers/TranslationService';
import RequestFormPersonalInfo_Row from '../RequestFormPersonalInfo/RequestFormPersonalInfo_Row';
import { CountryCodeSelect, ICountryCodeValue } from '../DynamicForm/CountryCodeSelect';
import { useActions } from '../../hooks/useActions';
import {
    useContactUsFormValidation,
    ContactUsFieldsEnumKeys,
} from './useRequestContactUsFormValidation';
import { format } from 'date-fns';

import {
    InputLabel,
    InputWrapper,
    ModalContent,
    ModalFooter,
    ModalHeader,
    NameRow,
    PhoneRow,
    ModalButton,
    StyledModal,
    StyledCrossIcon,
    CloseButton,
    ModalTitle,
    ScrollWrapper,
    NewsletterSubscription,
    PersonalDataLegalText,
    LegalWrapper,
    StyledLabel,
    StyledCheckbox,
    GenderRow,
    GenderWrapper,
    GenderRadioButton,
    ButtonWrapper,
    ErrorWrapper,
    StyledWarningIcon,
    ErrorText,
} from './CruiseInquiryModal.style';
import { ErrorsComponent } from '../RequestFormPersonalInfo/ControlComponents';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import { eventTracker } from '../../helpers/EventTracker/EventTracker';
import { ICruiseReducer } from '../../interfaces';
import { IabTests } from '../../reducers/abTests';
import { getTrackingAbTestsValues } from '../../middleware/trackingHelpers';

enum GenderTypes {
    male = 'male',
    female = 'female',
}
interface IProps {
    onClose: () => void;
    cruisePage: ICruiseReducer;
    abTests: IabTests;
    defaultCurrency?: string;
    isAllInclusive?: boolean;
}

export const CruiseInquiryModal = ({ cruisePage, abTests, onClose, ...props }: IProps) => {
    const { ui, requestForm } = useTypedSelector((state) => state);
    const { isSubmitError, apiValidationErrors } = requestForm;
    const { isLoadingGlobal } = ui;
    const controlsEnum = useContactUsFormValidation({
        validateNow: false,
    });
    const { submitContactUsForm } = useActions();
    const [genderSelectedVal, setGenderValue] = React.useState('');
    if (!controlsEnum) return null;

    const countryCodeOptions = controlsEnum[
        ContactUsFieldsEnumKeys.countryCode
    ].control.options.map((o) => {
        if (typeof o?.value === 'string' && o.value.startsWith('+')) {
            return { ...o, label: o.value };
        }
        return o;
    });

    const handleGenderRadioChange = (type: GenderTypes) => {
        controlsEnum[ContactUsFieldsEnumKeys.isFemale].control.onBlur({
            target: {
                type: 'radio',
                checked: type === GenderTypes.female,
            },
        });
    };

    const handleCountryChange = (countryCodeOption) => {
        controlsEnum[ContactUsFieldsEnumKeys.countryCode].control.onBlur({
            target: { value: countryCodeOption.value },
        });
    };

    const replaceAsterisk = (str: string) => str.replace(/\s+[*]|[*]/, '');

    const submitForm = () => {
        const errors = [];

        for (const field in controlsEnum) {
            controlsEnum[field].control.setDirty(true);

            if (!controlsEnum[field].control.isValid) {
                errors.push(controlsEnum[field].name);
            }
        }

        if (errors.length > 0) {
            return;
        }

        submitContactUsForm();
    };

    const genderControlValue = controlsEnum[ContactUsFieldsEnumKeys.isFemale].control.value;

    React.useEffect(() => {
        if (genderControlValue === true) {
            setGenderValue(GenderTypes.female);
        }
        if (genderControlValue === false) {
            setGenderValue(GenderTypes.male);
        }
    }, [genderControlValue]);

    React.useEffect(() => {
        const portOfDisembarkation =
            Array.isArray(cruisePage.waypoints) &&
            cruisePage.waypoints.find((_) => _.isPortOfDisembarkation);

        const portOfEmbarkation =
            Array.isArray(cruisePage.waypoints) &&
            cruisePage.waypoints.find((_) => _.isPortOfEmbarkation);

        const flight = requestForm.includesFlight ? 1 : 0;
        const sail = cruisePage?.sails[cruisePage.selectedSailIndex];
        const cabin = cruisePage.cabins.find(
            (cabin) => cabin.cabinType && cabin.cabinType.nid === requestForm.cabinNid,
        );

        const selectedSailRates = cruisePage?.rates?.filter(
            (cabin) => cabin.sail_nid === cruisePage?.sails[cruisePage.selectedSailIndex]?.nid,
        );

        const rate = selectedSailRates.find((rate) => rate.cabintype_id === cabin.cabinType.nid);
        const ratePrice = props.isAllInclusive ? rate?.all_in_price : rate?.cheapest_price;

        eventTracker.trackG4Event('BookingFunnel', {
            type: 'inquiry',
            step: 'cruise-page',
            funnelLength: `flight: ${flight}, cabin: ${0}`,
            company_name: cruisePage.operator.title,
            cruise_name: cruisePage.title,
            ship_name: cruisePage.ship.title,
            nights_number: cruisePage.nights,
            product_id_1: cruisePage.nid,
            destination_name: cruisePage.zone,
            harbor_start: portOfEmbarkation?.title,
            harbor_end: portOfDisembarkation?.title,
            flight_option: requestForm.includesFlight ? 'incl' : '',
            quantity: requestForm.adultsNum,
            sail_id: requestForm.sailNid,
            date_start: sail?.departure ? format(new Date(sail.departure), 'dd.M.yyyy') : '',
            date_end: sail?.arrival ? format(new Date(sail.arrival), 'dd.M.yyyy') : '',
            currency: requestForm.perPersonPriceDefaultCurrency
                ? props.defaultCurrency
                : requestForm.currency || props.defaultCurrency,

            cabin_type: cabin?.cabinType.title,
            transactionTotal: ratePrice?.cabin_price,
            isEstimatedPrice: String(!!ratePrice?.isEstimatedPrice),
            abtest: Object.values(abTests || [])
                .map(getTrackingAbTestsValues)
                .join(','),
        });
    }, []);

    const hasGenderInputError =
        controlsEnum[ContactUsFieldsEnumKeys.isFemale].control.isDirty &&
        !controlsEnum[ContactUsFieldsEnumKeys.isFemale].control.isValid;

    const hasValidationErrors = apiValidationErrors?.length;

    return (
        <StyledModal isOpen={true} onClose={onClose}>
            <>
                <ModalHeader>
                    <ModalTitle>{__('contactUs.modalSubmit', 'dreamlines')}</ModalTitle>
                    <CloseButton onClick={onClose}>
                        <StyledCrossIcon />
                    </CloseButton>
                </ModalHeader>
                <ScrollWrapper>
                    <ModalContent>
                        <RequestFormPersonalInfo_Row
                            label={`${replaceAsterisk(
                                __('personalData.salutation', 'dreamlines'),
                            )}:`}
                            field={controlsEnum[ContactUsFieldsEnumKeys.isFemale]}
                        >
                            <GenderRow>
                                <GenderWrapper>
                                    <StyledLabel reverse={true}>
                                        {__('personalData.female', 'dreamlines')}
                                        <GenderRadioButton
                                            type="radio"
                                            name="genderCheckbox"
                                            checked={genderSelectedVal === GenderTypes.female}
                                            id="female"
                                            onChange={() =>
                                                handleGenderRadioChange(GenderTypes.female)
                                            }
                                        />
                                    </StyledLabel>
                                    <StyledLabel reverse={true}>
                                        {__('personalData.male', 'dreamlines')}
                                        <GenderRadioButton
                                            type="radio"
                                            name="genderCheckbox"
                                            id="male"
                                            checked={genderSelectedVal === GenderTypes.male}
                                            onChange={() =>
                                                handleGenderRadioChange(GenderTypes.male)
                                            }
                                        />
                                    </StyledLabel>
                                </GenderWrapper>
                                {hasGenderInputError && (
                                    <ErrorsComponent
                                        errors={[__('general.required', 'dreamlines')]}
                                    />
                                )}
                            </GenderRow>
                        </RequestFormPersonalInfo_Row>
                        <NameRow>
                            <RequestFormPersonalInfo_Row
                                label={replaceAsterisk(__('personalData.firstname', 'dreamlines'))}
                                type="text"
                                field={controlsEnum[ContactUsFieldsEnumKeys.firstname]}
                            />
                            <RequestFormPersonalInfo_Row
                                label={replaceAsterisk(__('personalData.lastname', 'dreamlines'))}
                                type="text"
                                field={controlsEnum[ContactUsFieldsEnumKeys.lastname]}
                            />
                        </NameRow>
                        <InputLabel htmlFor="phone">
                            {replaceAsterisk(__('personalData.phone', 'dreamlines'))}
                        </InputLabel>

                        <PhoneRow
                            hasError={
                                controlsEnum[ContactUsFieldsEnumKeys.phone].control.isDirty &&
                                !controlsEnum[ContactUsFieldsEnumKeys.phone].control.isValid
                            }
                        >
                            <CountryCodeSelect
                                isError={false}
                                onChange={handleCountryChange}
                                options={countryCodeOptions as ICountryCodeValue[]}
                                // in the end CustomSelect has value as any
                                value={
                                    controlsEnum[ContactUsFieldsEnumKeys.countryCode].control
                                        .value as any
                                }
                            />
                            <RequestFormPersonalInfo_Row
                                type="tel"
                                field={controlsEnum[ContactUsFieldsEnumKeys.phone]}
                            />
                        </PhoneRow>
                        <InputWrapper>
                            <RequestFormPersonalInfo_Row
                                label={replaceAsterisk(__('personalData.email', 'dreamlines'))}
                                type="email"
                                field={controlsEnum[ContactUsFieldsEnumKeys.email]}
                            />
                        </InputWrapper>
                        <InputWrapper optional={true}>
                            <RequestFormPersonalInfo_Row
                                label={`${__('personalData.message', 'dreamlines')}`}
                                field={controlsEnum[ContactUsFieldsEnumKeys.message]}
                            />
                        </InputWrapper>
                    </ModalContent>
                    <ModalFooter>
                        <LegalWrapper>
                            <StyledLabel htmlFor={ContactUsFieldsEnumKeys.newsletterSubscription}>
                                <StyledCheckbox
                                    type="checkbox"
                                    field={
                                        controlsEnum[ContactUsFieldsEnumKeys.newsletterSubscription]
                                    }
                                />
                                <NewsletterSubscription
                                    dangerouslySetInnerHTML={{
                                        __html: __('cruiseData.newsletterSub', 'dreamlines'),
                                    }}
                                />
                            </StyledLabel>

                            <PersonalDataLegalText
                                dangerouslySetInnerHTML={{
                                    __html: __('contactUs.privacyPolicy', 'dreamlines'),
                                }}
                            />
                        </LegalWrapper>
                        <ButtonWrapper>
                            <ModalButton loading={isLoadingGlobal} onClick={submitForm}>
                                {__('contactUs.modalSubmit', 'dreamlines')}
                            </ModalButton>

                            {isSubmitError && (
                                <ErrorWrapper>
                                    <StyledWarningIcon />

                                    {hasValidationErrors ? (
                                        <ErrorText>
                                            {__(apiValidationErrors[0].errorKey, 'dreamlines')}
                                        </ErrorText>
                                    ) : (
                                        <ErrorText>
                                            {__(
                                                'Something went wrong. Please try again later.',
                                                'dreamlines',
                                            )}
                                        </ErrorText>
                                    )}
                                </ErrorWrapper>
                            )}
                        </ButtonWrapper>
                    </ModalFooter>
                </ScrollWrapper>
            </>
        </StyledModal>
    );
};
